"use client"
import { CarModelMainDto } from "api"
import { useIsDesktop } from "common/MediaQueries"
import { useScrollDirection } from "common/bit/hooks/useScrollDirection"
import {
  ScrollingProviderWithSuspense as ScrollingProvider,
  Section,
} from "common/bit/scrollSection"
import FAQComp from "components/faq/FAQComp"
import { setModel } from "components/shopFeatures/detailing/detailingSlice"
import { useNavLinks } from "contexts/NavLinksContext"
import { useScroll } from "framer-motion"
import Header from "layout/header/Header"
import dynamic from "next/dynamic"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import styled from "styled-components"
import { keyFrameFadeOut } from "theme/animation"
import { HEADER_ANIME_D, HEADER_ANIME_M } from "theme/shared"
import ModelPageTopBanner from "./TopBanner/ModelPageTopBanner"
import CarModelNavBar from "./navigation/CarModelNavBar"
import StickyBottomCTA from "./navigation/StickyBottomCTA"
import DrivingExperienceSection from "./sections/drivingExperience/DrivingExperienceSection"
import ExteriorSection from "./sections/exterior/ExteriorSection"
import FinishingLevelsSection from "./sections/finishingLevels/FinishingLevelsSection"
import IllustrateSizeSection from "./sections/illustrateSize/IllustrateSizeSection"
import InteriorSection from "./sections/interior/InteriorSection"
import ReviewsSection from "./sections/review/ReviewsSection"
import SafetySection from "./sections/safety/SafetySection"
import SpecificationsSection from "./sections/specifications/SpecificationsSection"

const Finance = dynamic(() => import("reusable/finance/FinanceWrapper"), {
  ssr: false,
})

const StyledPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledHeaderCarPage = styled(Header)<{ $exit: boolean }>`
  animation: ${(p) => p.$exit && keyFrameFadeOut} 0.5s ease-in-out forwards;
  display: ${(p) => (p.$exit ? "none" : "flex")};
`

const StyledCarModelNavBar = styled(CarModelNavBar)<{
  $exit: boolean
  $isPastThreshold: boolean
}>`
  z-index: ${(p) => p.$exit && "1251"};
  transform: ${(p) => !p.$exit && p.$isPastThreshold && "translateY(50px)"};
  transition: ${(p) =>
    !p.$exit &&
    p.$isPastThreshold &&
    "all 225ms cubic-bezier(0, 0, 0.2, 1) 0ms"};
  display: ${(p) => (p.$exit || p.$isPastThreshold ? "flex" : "none")};

  ${(p) => p.theme.breakpoints.up("smallLaptop")} {
    transform: ${(p) =>
      !p.$exit && p.$isPastThreshold && `translateY(${HEADER_ANIME_D}px)`};
  }
`

type CarModelPageProps = {
  data: CarModelMainDto
}

const CarModelPage = ({ data }: CarModelPageProps) => {
  const [popupOpen, setPopupOpen] = useState<boolean>(false)
  const [pageLoaded, setPageLoaded] = useState<boolean>(false)
  const sumOfAllFinishingLevels = data?.finishingLevels?.engines?.reduce(
    (acc, curr) => acc + curr.finishingLevels.length,
    0
  )
  const isDesktop = useIsDesktop()
  // Same CMS nav the shared Layout feeds the homepage header; Layout hides its
  // header on model routes, so we mount <Header> ourselves and supply the links
  // here so the mobile drawer isn't empty (DLK-845).
  const navLinks = useNavLinks()
  const scrollDirection = useScrollDirection()
  // Value being driven by scrolling (e.g. height)
  const thresholdY = 2 // set the scroll position where you want the state change
  const { scrollY } = useScroll()
  const [isPastThreshold, setIsPastThreshold] = useState(false)

  useEffect(
    () =>
      scrollY.onChange((latest) => {
        setIsPastThreshold(
          scrollDirection === "up" && latest <= 1000
            ? false
            : latest > thresholdY
        )
      }),
    []
  )
  // use effect to set page loaded to true after 1 second
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true)
    }, 1000)
    return () => clearTimeout(timer)
  }, [])

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(setModel(data.generalInfo.id.toString()))
  }, [data.generalInfo.id])

  return (
    <>
      {pageLoaded && (
        <Finance
          modelId={data.generalInfo.id}
          modelName={data.generalInfo.name}
          popupOpen={popupOpen}
          setPopupOpen={setPopupOpen}
        />
      )}

      <ScrollingProvider offset={isDesktop ? HEADER_ANIME_D : HEADER_ANIME_M}>
        <StyledPageWrapper>
          <StyledCarModelNavBar
            modelName={data?.generalInfo?.name}
            $exit={scrollDirection === "down"}
            $isPastThreshold={isPastThreshold}
          />
          <StyledHeaderCarPage
            narrow={true}
            links={navLinks}
            $exit={scrollDirection === "down"}
          />
          <ModelPageTopBanner data={data.topBannerDto} />
          {data?.outside && (
            <Section id="exterior" title="חוץ">
              <ExteriorSection
                data={data}
                setCalculatorPopupOpen={setPopupOpen}
              />
            </Section>
          )}
          {data?.inside && (
            <Section id="interior" title="פנים">
              <InteriorSection insideData={data?.inside} />
            </Section>
          )}
          {data?.volumeIllustration && (
            <Section id="illustrate-size" title="המחשת נפח">
              <IllustrateSizeSection
                illustrateSize={
                  data?.volumeIllustration.volumeIllustrationElements
                }
              />
            </Section>
          )}
          {(!!data?.drivingExpirience?.engine?.length ||
            !!data?.drivingExpirience?.displayComponents?.length) && (
            <Section id="driving-experience" title="חוויית נהיגה">
              <DrivingExperienceSection
                drivingExpirience={data?.drivingExpirience}
                modelName={data?.generalInfo?.name}
                modelImge={data?.generalInfo?.image?.imageUrl}
              />
            </Section>
          )}

          {data?.safety && (
            <Section id="safety" title="בטיחות">
              <SafetySection data={data?.safety} />
            </Section>
          )}

          {data?.finishingLevels?.engines?.length > 0 && (
            <Section id="finishing_levels" title="רמות גימור">
              <FinishingLevelsSection
                setCalculatorPopupOpen={setPopupOpen}
                data={data?.finishingLevels}
                modelName={data?.generalInfo?.name}
              />
            </Section>
          )}
          {data.articles && (
            <Section id="reviews" title="סקירות">
              <ReviewsSection carData={data} />
            </Section>
          )}
          {data?.faq?.isActive && data.faq.faqLists?.length > 0 && (
            <Section id="faq" title="שאלות נפוצות">
              <FAQComp data={data.faq} withMobilePadding={true} />
            </Section>
          )}
          {(data?.regulation || data?.catalogs) && (
            <Section id="specifications" title="מפרטים">
              <SpecificationsSection carData={data} />
            </Section>
          )}
          <StickyBottomCTA
            setPopupOpen={setPopupOpen}
            numberOfFinishingLevels={sumOfAllFinishingLevels}
            modelId={data.generalInfo.id}
            isPreSale={data.generalInfo?.typeId === 3}
            isDriveTestAvailable={data.isDriveTestAvailable}
            showroomMeetingUrl={data.showroomMeetingUrl}
            driveTestUrl={data.driveTestUrl}
          />
        </StyledPageWrapper>
      </ScrollingProvider>
    </>
  )
}

export default CarModelPage
